import React, { useEffect, useState, useCallback, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
    Tabs,
    Tab,
    Typography,
    Grid,
    Hidden,
    List,
    ListItem,
    ListItemText,
    Dialog,
    DialogActions,
    Snackbar,
    SnackbarContent,
    IconButton,
    Card,
    CardContent,
    Tooltip,
    ListItemSecondaryAction,
    DialogTitle,
    DialogContent,
    DialogContentText,
    FormControlLabel,
    Checkbox,
} from '@material-ui/core';
import MaterialTable, { MTableBodyRow } from 'material-table';
import Refresh from '@material-ui/icons/Refresh';
import DeleteIcon from '@material-ui/icons/Delete';
import Divider from '../../controls/Divider';
import moment from 'moment';
import DataEditor from '../../controls/DataEditor';
import Button from '../../controls/Button';
import { apiGet, apiPost, apiDelete } from '../../api';
import { useAccessTokens, useUserProfile } from '../../Authenticate';

const useStyles = makeStyles((theme) => ({
    table: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(3),
    },
    tableRow: {
        '&:hover $messageAction': {
            visibility: 'visible',
        },
    },
    card: {
        width: '55vw',
    },
    configureBody: {
        margin: theme.spacing(2),
    },
    tabLabel: {
        '&:hover': {
            color: theme.palette.primary.main,
        },
        minWidth: theme.spacing(0),
        padding: theme.spacing(0),
        textTransform: 'none',
        alignItems: 'center',
        fontSize: 16,
    },
    tabBar: {
        display: 'flex',
        justifyContent: 'space-between',
    },
    tabsRoot: {
        width: 'calc(100% - 70px)',
    },
    tabsFlexContainer: {
        gap: '3%',
        alignItems: 'center',
        paddingLeft: theme.spacing(3),
    },
    whiteBackground: {
        backgroundColor: 'white',
    },
    messageTitle: {
        color: '#6E7788',
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),
    },
    messageDetailPanel: {
        paddingLeft: theme.spacing(4),
        paddingRight: theme.spacing(2),
        color: '#484848',
    },
    messageTablePanel: {
        borderRight: '1.25px solid rgba(0, 0, 0, 0.12)',
    },
    messageText: {
        overflowWrap: 'break-word',
        whiteSpace: 'pre-wrap',
    },
    listItemText: { display: 'grid' },
    dialog: {
        margin: theme.spacing(3),
    },
    messageAction: {
        visibility: 'hidden',
    },
}));

export default function Messages() {
    const classes = useStyles();
    const { getAccessToken, authorizationScopes } = useAccessTokens();
    const userProfile = useUserProfile();

    const ERROR_TAB = 0;
    const ACTIVE_TAB = 1;

    const [activeMessages, setActiveMessages] = useState([]);
    const [deadLetteredMessages, setDeadLetteredMessages] = useState([]);

    const [currentTab, setCurrentTab] = useState(ERROR_TAB);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [disableDeleteDialogWarning, setDisableDeleteDialogWarning] = useState(false);
    const [snackbarInfo, setSnackbarInfo] = useState({ message: 'message', open: false, color: '#000000' });

    const messageRef = useRef();

    const messagesList =
        currentTab === ACTIVE_TAB ? activeMessages : currentTab === ERROR_TAB ? deadLetteredMessages : [];

    const isInternalAdmin = !!userProfile?.roles?.includes('InternalAdminUser');

    const hasWritePermissions = currentTab === ERROR_TAB && isInternalAdmin;

    const selectMessage = (message) => {
        messageRef.current = message;

        setDialogOpen(true);
    };

    const loadActiveMessages = useCallback(async () => {
        const token = await getAccessToken(authorizationScopes.queues);

        try {
            const messages = await apiGet('/queues/__import__/messages', token, {
                params: { filter: { order: ['enqueuedTimeUtc DESC'] } },
            });

            setActiveMessages(messages.data);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.log('Messages Error: ', err.response.data.error.message);
        }
    }, [authorizationScopes, getAccessToken, setActiveMessages]);

    const loadDeadLetteredMessages = useCallback(async () => {
        const token = await getAccessToken(authorizationScopes.queues);

        try {
            const messages = await apiGet('/queues/__import__/deadLetters', token, {
                params: { filter: { order: ['enqueuedTimeUtc DESC'] } },
            });

            setDeadLetteredMessages(messages.data);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.log('DeadLettered Messages Error: ', err.response.data.error.message);
        }
    }, [authorizationScopes, getAccessToken, setDeadLetteredMessages]);

    useEffect(() => {
        (async function () {
            if (currentTab === ACTIVE_TAB) {
                await loadActiveMessages();
            } else {
                await loadDeadLetteredMessages();
            }
        })();

        messageRef.current = undefined;
    }, [currentTab, loadActiveMessages, loadDeadLetteredMessages]);

    const refreshMessages = async () => {
        if (currentTab === ACTIVE_TAB) {
            await loadActiveMessages();
        } else {
            await loadDeadLetteredMessages();
        }

        messageRef.current = undefined;
    };

    const handleDelete = async (id) => {
        if (!disableDeleteDialogWarning) {
            setDeleteDialogOpen(true);
        } else {
            await deleteMessage(id);

            messageRef.current = undefined;
        }
    };

    const MessageTable = () => {
        return (
            <MaterialTable
                options={{
                    toolbar: false,
                    paging: false,
                    draggable: false,
                }}
                components={{
                    Container: ({ children }) => <div className={classes.table}>{children}</div>,
                    Row: (props) => <MTableBodyRow className={classes.tableRow} {...props} />,
                }}
                style={{ boxShadow: 'none', paddingLeft: 24, paddingRight: 24 }}
                localization={{
                    body: {
                        emptyDataSourceMessage: 'No Messages To Display',
                    },
                }}
                columns={[
                    {
                        title: 'Date',
                        field: 'enqueuedTimeUtc',
                        type: 'datetime',
                        render: (row) => moment(row.enqueuedTimeUtc).format('MM/DD/YYYY HH:mm'),
                        // TODO: enable sorting to allow viewing messages in reverse. There is a bug
                        // in the material table where it doesn't keep the sorting state during rerender
                        // so when the modal opens up the list is reverted to its original sort order.
                        // A workaround will need to be found to enable sorting.
                        sorting: false,
                    },
                    { title: 'Application ID', field: 'body.source.submissionId', sorting: false },
                    hasWritePermissions
                        ? {
                              render: (row) => (
                                  <Tooltip className={classes.messageAction} title="Delete">
                                      <IconButton
                                          aria-label="delete"
                                          onClick={async (event) => {
                                              event.stopPropagation();

                                              messageRef.current = row;

                                              await handleDelete(row.id);
                                          }}
                                      >
                                          <DeleteIcon color="action" />
                                      </IconButton>
                                  </Tooltip>
                              ),
                              cellStyle: {
                                  textAlign: 'right',
                                  padding: 0,
                              },
                              headerStyle: {
                                  visibility: 'hidden',
                              },
                              width: '48px',
                              sorting: false,
                          }
                        : {},
                ]}
                data={messagesList}
                onRowClick={(evt, row) => {
                    selectMessage(row);
                }}
            />
        );
    };

    const MessageList = () => {
        return (
            <List>
                {messagesList?.length ? (
                    messagesList.map((row) => (
                        <ListItem key={row.id} button onClick={() => selectMessage(row)}>
                            <ListItemText
                                primary={
                                    <Typography>
                                        Date: {moment(row.enqueuedTimeUtc).format('MM/DD/YYYY HH:MM')}
                                    </Typography>
                                }
                                secondary={
                                    <Typography component="span" className={classes.listItemText}>
                                        <Typography>Application ID: {` ${row.body?.source?.submissionId}`}</Typography>
                                    </Typography>
                                }
                            />
                            {hasWritePermissions && (
                                <ListItemSecondaryAction>
                                    <Tooltip title="Delete">
                                        <IconButton
                                            aria-label="delete"
                                            onClick={async (event) => {
                                                event.stopPropagation();

                                                messageRef.current = row;

                                                await handleDelete(row.id);
                                            }}
                                        >
                                            <DeleteIcon color="action" />
                                        </IconButton>
                                    </Tooltip>
                                </ListItemSecondaryAction>
                            )}
                        </ListItem>
                    ))
                ) : (
                    <ListItem>
                        <Typography>No Messages To Display</Typography>
                    </ListItem>
                )}
            </List>
        );
    };

    const deleteMessage = async (id) => {
        const token = await getAccessToken(authorizationScopes.queues);

        try {
            await apiDelete(`queues/__import__/deadLetters/${id}`, token);
        } catch (err) {
            // If the response is 404 we can assume the message was
            // already deleted and don't need to display an error.
            if (err.response.status !== 404) {
                setSnackbarInfo({ message: 'Unable to delete dead-letter message', open: true, color: '#f44336' });

                return;
            }
        }

        // TODO: Remove message from list without having to load all messages again
        // since if there are many messages the requeuing/deleting can be delayed
        // significantly.
        // Removing the message from 'messagesList' was not working properly since
        // the material table seemed to not be updated right away, leaving the deleted
        // message still displayed in the list.
        await refreshMessages();
    };

    const requeueMessage = async (data) => {
        const token = await getAccessToken(authorizationScopes.queues);

        const count = (
            await apiGet('queues/__import__/deadLetters/count', token, {
                params: { where: { id: data.id } },
            })
        ).data.count;

        // If the dead letter message does not exist assume it was already
        // deleted.
        if (!count) {
            setSnackbarInfo({
                message: 'Message may already have been requeued or deleted',
                open: true,
                color: 'yellow',
            });

            setDialogOpen(false);

            return;
        }

        const { id, ...rest } = data.body;

        await apiPost('queues/__import__/messages', rest, token);

        await deleteMessage(data.id);

        setSnackbarInfo({ message: 'Message requeued', open: true, color: '#66bb6a' });

        setDialogOpen(false);
    };

    const snackBar = (
        <Snackbar
            open={snackbarInfo.open}
            autoHideDuration={1500}
            onClose={() => setSnackbarInfo({ ...snackbarInfo, open: false })}
        >
            <SnackbarContent
                style={{
                    backgroundColor: snackbarInfo.color,
                }}
                message={snackbarInfo.message}
            />
        </Snackbar>
    );

    const openEditor = () => {
        const { source, ...rest } = messageRef.current?.body ?? {};
        return (
            <Dialog open={dialogOpen} fullWidth={true} maxWidth="md" className={classes.dialog}>
                <Grid container>
                    <Typography variant="h6" className={classes.dialog}>
                        {messageRef.current?.body.source.name} - {messageRef.current?.body.source.submissionId}
                    </Typography>
                </Grid>

                <DataEditor
                    data={rest}
                    handleUpdate={
                        hasWritePermissions
                            ? (data) =>
                                  (messageRef.current = {
                                      ...messageRef.current,
                                      body: { source: messageRef.current?.body.source, ...data },
                                  })
                            : () => null
                    }
                    readOnly={!hasWritePermissions}
                />

                <DialogActions className={classes.dialog}>
                    <Button
                        variant="outlined"
                        onClick={() => {
                            messageRef.current = undefined;

                            setDialogOpen(false);
                        }}
                    >
                        Cancel
                    </Button>
                    {hasWritePermissions && (
                        <Button
                            variant="containedPrimary" // This is where error comes from.
                            onClick={async () => requeueMessage(messageRef.current)}
                        >
                            Requeue
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        );
    };

    const toggleDisableDeleteDialogWarning = (e) => {
        setDisableDeleteDialogWarning(e.target.checked);
    };

    const deleteDialog = (
        <Dialog open={deleteDialogOpen}>
            <DialogTitle>Wait!</DialogTitle>
            <DialogContent>
                <DialogContentText id="deleteMessage">
                    Are you sure you want to permanently delete this message?
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <FormControlLabel
                    label="Hide future warnings for current session"
                    labelPlacement="start"
                    control={
                        <Checkbox
                            color="primary"
                            checked={disableDeleteDialogWarning}
                            onChange={(e) => toggleDisableDeleteDialogWarning(e)}
                        />
                    }
                />

                <Button
                    onClick={() => {
                        messageRef.current = undefined;

                        setDeleteDialogOpen(false);
                    }}
                    color="primary"
                >
                    Cancel
                </Button>
                <Button
                    onClick={async () => {
                        await deleteMessage(messageRef.current.id);

                        messageRef.current = undefined;

                        setDeleteDialogOpen(false);
                    }}
                    variant="contained"
                    color="primary"
                >
                    Delete
                </Button>
            </DialogActions>
        </Dialog>
    );

    return (
        <Grid container justifyContent="center" alignItems="center">
            {isInternalAdmin && snackBar}
            {openEditor()}
            {isInternalAdmin && deleteDialog}
            <div className={classes.configureBody}>
                <Card className={classes.card}>
                    <CardContent>
                        <div className={classes.tabBar}>
                            <Tabs
                                value={currentTab}
                                variant="scrollable"
                                indicatorColor="primary"
                                textColor="primary"
                                classes={{ root: classes.tabsRoot, flexContainer: classes.tabsFlexContainer }}
                            >
                                <Tab
                                    label={
                                        <Typography variant="button" className={classes.tabLabel}>
                                            Error
                                        </Typography>
                                    }
                                    className={classes.tabLabel}
                                    onClick={() => setCurrentTab(ERROR_TAB)}
                                />
                                <Tab
                                    label={
                                        <Typography variant="button" className={classes.tabLabel}>
                                            Active
                                        </Typography>
                                    }
                                    className={classes.tabLabel}
                                    onClick={() => setCurrentTab(ACTIVE_TAB)}
                                />
                            </Tabs>
                            <IconButton aria-label="Refresh" onClick={async () => refreshMessages()}>
                                <Refresh />
                            </IconButton>
                        </div>
                        <Divider />
                        <Hidden mdDown>
                            <MessageTable />
                        </Hidden>
                        <Hidden lgUp>
                            <MessageList />
                        </Hidden>
                    </CardContent>
                </Card>
            </div>
        </Grid>
    );
}
